import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import type { ReactNode } from 'react';
import {
  UILanguage,
  type TranslationKeys,
  getTranslations,
  getTranslationValue,
  loadUILanguageFromStorage,
  saveUILanguageToStorage,
  UI_LANGUAGES,
  type UILanguageInfo
} from '@/i18n';

interface I18nContextType {
  // Current UI language
  currentLanguage: UILanguage;
  
  // All translations for current language
  translations: TranslationKeys;
  
  // Function to change UI language
  changeLanguage: (language: UILanguage) => void;
  
  // Function to get translation by key path
  t: (keyPath: string, fallback?: string) => string;
  
  // Available languages
  availableLanguages: UILanguageInfo[];
  
  // Current language info
  currentLanguageInfo: UILanguageInfo;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: ReactNode;
  defaultLanguage?: UILanguage;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ 
  children, 
  defaultLanguage 
}) => {
  // Initialize language from storage or default
  const [currentLanguage, setCurrentLanguage] = useState<UILanguage>(() => {
    if (defaultLanguage) {
      return defaultLanguage;
    }
    return loadUILanguageFromStorage();
  });

  // Get translations for current language
  const translations = useMemo(() => {
    return getTranslations(currentLanguage);
  }, [currentLanguage]);

  // Get current language info
  const currentLanguageInfo = useMemo(() => {
    return UI_LANGUAGES[currentLanguage];
  }, [currentLanguage]);

  // Get available languages
  const availableLanguages = useMemo(() => {
    return Object.values(UI_LANGUAGES);
  }, []);

  // Translation function
  const t = useCallback((keyPath: string, fallback?: string): string => {
    return getTranslationValue(translations, keyPath, fallback);
  }, [translations]);

  // Change language function
  const changeLanguage = useCallback((language: UILanguage) => {
    console.log(`🌐 [I18nContext] Changing UI language from ${currentLanguage} to ${language}`);
    
    setCurrentLanguage(language);
    saveUILanguageToStorage(language);
    
    // Update document language attribute
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }, [currentLanguage]);

  // Set initial document language
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = currentLanguage;
    }
  }, [currentLanguage]);

  // Context value
  const value = useMemo((): I18nContextType => ({
    currentLanguage,
    translations,
    changeLanguage,
    t,
    availableLanguages,
    currentLanguageInfo
  }), [
    currentLanguage,
    translations,
    changeLanguage,
    t,
    availableLanguages,
    currentLanguageInfo
  ]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

/**
 * Hook to use I18n context
 */
export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

/**
 * Hook to get translation function only (optimized for performance)
 */
export const useTranslation = () => {
  const { t } = useI18n();
  return { t };
};
